import React, { useEffect, useState, useCallback } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Table,              
  TableBody,          
  TableCell,          
  TableContainer,     
  TableHead,          
  TableRow,   
  Collapse,
  TextField,
  InputAdornment,
  Pagination
} from "@mui/material";

// MUI Icons
import {
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  CalendarToday as CalendarTodayIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Home as HomeIcon,
  MoreVert as MoreVertIcon,
  NoteAdd as NoteAddIcon,
  Palette as PaletteIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Straighten as StraightenIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from "@mui/icons-material";
import AddIcon from '@mui/icons-material/Add';
import NotesModal from "./_notesModal";
import { ENDPOINTS } from '../config'; 
import { createManualQuote } from '../services/apiService';
import { t } from '../i18n/useTranslation';

const QuoteManagement = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openRow, setOpenRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortCriteria, setSortCriteria] = useState("timeline");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [quoteToUpdate, setQuoteToUpdate] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [selectedTimeframe, setSelectedTimeframe] = useState("all");
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuQuoteId, setMenuQuoteId] = useState(null);
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [manualData, setManualData] = useState({
      customerName: '', email: '', phone: '', address: '',
      productType: 'Canopy - Wall Mounted', estimatedValue: '', notes: '',
      width: '', length: '', color: ''
  });
  const [viewMode, setViewMode] = useState('card');
  const [searchQuery, setSearchQuery] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const clearQuotesCache = () => {
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('alfresko_quotes_page_')) {
        sessionStorage.removeItem(key);
      }
    });
    sessionStorage.removeItem('alfresko_dashboard_all_quotes');
  };

  const fetchQuotes = useCallback(async (currentPage = 1) => {
    const cacheKey = `alfresko_quotes_page_${currentPage}`;
    
    try {
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        setQuotes(parsed.quotesArray);
        if (parsed.pagination) {
          setTotalPages(parsed.pagination.totalPages);
          setTotalCount(parsed.pagination.totalCount);
        }
        setLoading(false); 
      } else {
        setLoading(true);
      }

      const token = localStorage.getItem('authToken');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const url = `${ENDPOINTS.DEALER_PORTAL.MY_QUOTES}?page=${currentPage}&pageSize=20`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      
      if (response.status === 401) {
        window.location.href = '/login';
        return;
      }

      if (!response.ok) throw new Error(`Error: ${response.status}`);
      
      const json = await response.json();
      const quotesArray = json.data || (Array.isArray(json) ? json : []);
      
      sessionStorage.setItem(cacheKey, JSON.stringify({
        quotesArray,
        pagination: json.pagination
      }));

      setQuotes(quotesArray);
      if (json.pagination) {
        setTotalPages(json.pagination.totalPages);
        setTotalCount(json.pagination.totalCount);
      } else {
        setTotalCount(quotesArray.length);
      }

    } catch (error) {
      console.error("Failed to fetch quotes:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuotes(page);
  }, [fetchQuotes, page]);

  const handleStatusChange = async () => {
    if (!quoteToUpdate || !newStatus) return;

    const idToUpdate = quoteToUpdate; 
    const originalQuotes = [...quotes]; 

    setQuotes(prevQuotes =>
      prevQuotes.map(quote =>
        quote.id === idToUpdate ? { ...quote, status: newStatus } : quote
      )
    );
    handleCloseStatusDialog(); 

    try {
        const response = await fetch(`${ENDPOINTS.QUOTES}/${idToUpdate}`, {
            method: 'PATCH', 
            headers: getAuthHeaders(),
            body: JSON.stringify({ status: newStatus.toUpperCase() }), 
        });

        if (!response.ok) {
            const errorBody = await response.text();
            throw new Error(`Server responded with ${response.status}. Body: ${errorBody}`);
        }
        
        showSuccessSnackbar(t('quotes.success.statusUpdated'));
        clearQuotesCache();

    } catch (error) {
        console.error("Failed to update status:", error);
        setSuccessMessage("Failed to update the status. The change has been reverted.");
        setSnackbarOpen(true);
        setQuotes(originalQuotes); 
    }
  };

  const handleDeleteQuote = async () => {
    if (!quoteToDelete) return;
    const originalQuotes = [...quotes];
    
    setQuotes(prevQuotes => prevQuotes.filter(quote => quote.id !== quoteToDelete));
    handleCloseDeleteDialog();

    try {
      const response = await fetch(`${ENDPOINTS.QUOTES}/${quoteToDelete}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}: ${response.statusText}`);
      }

      setSuccessMessage(t('quotes.success.deleted'));
      clearQuotesCache();
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to delete quote:", error);
      setSuccessMessage("Failed to delete the quote. The item has been restored.");
      setSnackbarOpen(true);
      setQuotes(originalQuotes);
    }
  };

  const handleOpenDeleteDialog = (id) => {
    setQuoteToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setQuoteToDelete(null);
    setDeleteDialogOpen(false);
  };

  const toggleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(quotes.map((quote) => quote.id));
    } else {
      setSelectedRows([]);
    }
  };

  const sortQuotes = (data, criteria) => {
    let sortedData = [...data];

    switch(criteria) {
      case "timeline":
        return sortedData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case "name":
        return sortedData.sort((a, b) => {
          const nameA = a.customer?.name || '';
          const nameB = b.customer?.name || '';
          return nameA.localeCompare(nameB);
        });
      case "price":
        return sortedData.sort((a, b) => {
          const priceA = a.totalPrice || 0;
          const priceB = b.totalPrice || 0;
          return priceB - priceA;
        });
      case "timeframe":
        return sortedData.sort((a, b) => {
          const statusA = a.status || '';
          const statusB = b.status || '';
          return statusA.localeCompare(statusB);
        });
      default:
        return sortedData;
    }
  };

  const handleTimeframeChange = (event) => {
    const timeframe = event.target.value;
    setSelectedTimeframe(timeframe);
    
    let filtered;
    if (timeframe === "all") {
      filtered = [...quotes];
    } else {
      filtered = quotes.filter(quote => quote.status === timeframe);
    }
    
    const sortedAndFiltered = sortQuotes(filtered, sortCriteria);
    setFilteredQuotes(sortedAndFiltered);
  };

  const handleSortChange = (event) => {
    const selectedCriteria = event.target.value;
    setSortCriteria(selectedCriteria);

    if (selectedCriteria !== "timeframe") {
      setSelectedTimeframe("all");
      setFilteredQuotes(quotes);
    }

    const dataToSort = selectedCriteria === "timeframe" ? filteredQuotes : quotes;
    const sortedData = sortQuotes(dataToSort, selectedCriteria);
    
    if (selectedCriteria === "timeframe") {
      setFilteredQuotes(sortedData);
    } else {
      setQuotes(sortedData);
    }
  };

  const handleOpenStatusDialog = (id, status) => {
    setQuoteToUpdate(id);
    setNewStatus(status);
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    setQuoteToUpdate(null);
    setNewStatus("");
    setStatusDialogOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleOpenNotesModal = (quote) => {
    setSelectedQuote(quote);
    setNotesModalOpen(true);
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
    setSelectedQuote(null);
  };

  const handleSaveNotes = (quoteId, newNotes) => {
    setQuotes(prevQuotes => 
      prevQuotes.map(q => 
        q.id === quoteId ? { ...q, internalNotes: newNotes } : q
      )
    );
  };

  const showSuccessSnackbar = (message) => {
    setSuccessMessage(message);
    setSnackbarOpen(true);
  };

  const handleMenuClick = (event, quoteId) => {
    setAnchorEl(event.currentTarget);
    setMenuQuoteId(quoteId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuQuoteId(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING": return "warning";
      case "CONTACTED": return "info";
      case "LEFT_MESSAGE": return "info";
      case "SURVEY_BOOKED": return "primary";
      case "SURVEY_COMPLETED": return "primary";
      case "REVISED_ESTIMATE_SENT": return "secondary";
      case "SALE_AGREED": return "success";
      case "INVOICED": return "success";
      case "PAYMENT_RECEIVED": return "success";
      case "ORDERED": return "primary";
      case "INSTALLED": return "success";
      case "COMPLETE": return "success";
      case "APPROVED": return "success";
      case "REJECTED": return "error";
      default: return "default";
    }
  };

  const getStatusThemeColor = (status) => {
    switch (status) {
      case "PENDING": return { bg: '#ffe0b2', text: '#b78103' }; // Warm Orange
      case "CONTACTED": return { bg: '#e0f2fe', text: '#0369a1' }; // Sky Blue
      case "LEFT_MESSAGE": return { bg: '#f1f5f9', text: '#475569' }; // Slate Gray
      case "SURVEY_BOOKED": return { bg: '#e0f7fa', text: '#006064' }; // Cyan
      case "SURVEY_COMPLETED": return { bg: '#e8f5e9', text: '#2e7d32' }; // Green
      case "REVISED_ESTIMATE_SENT": return { bg: '#f3e5f5', text: '#6a1b9a' }; // Purple
      case "SALE_AGREED": return { bg: '#d4edda', text: '#155724' }; // Soft Success
      case "INVOICED": return { bg: '#fff3cd', text: '#856404' }; // Gold
      case "PAYMENT_RECEIVED": return { bg: '#d1e7dd', text: '#0f5132' }; // Dark Success
      case "ORDERED": return { bg: '#e2e3e5', text: '#383d41' }; // Muted Gray
      case "INSTALLED": return { bg: '#cff4fc', text: '#055160' }; // Aqua
      case "COMPLETE": return { bg: '#d1e7dd', text: '#0f5132' }; // Solid Green
      case "APPROVED": return { bg: '#d1e7dd', text: '#0f5132' };
      case "REJECTED": return { bg: '#f8d7da', text: '#721c24' }; // Soft Danger
      default: return { bg: '#f8f9fa', text: '#333333' };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "PENDING": return "⏳";
      case "CONTACTED": return "📞";
      case "LEFT_MESSAGE": return "💬";
      case "SURVEY_BOOKED": return "📅";
      case "SURVEY_COMPLETED": return "✏️";
      case "REVISED_ESTIMATE_SENT": return "📤";
      case "SALE_AGREED": return "🤝";
      case "INVOICED": return "🧾";
      case "PAYMENT_RECEIVED": return "💰";
      case "ORDERED": return "📦";
      case "INSTALLED": return "🔧";
      case "COMPLETE": return "✅";
      case "APPROVED": return "✅";
      case "REJECTED": return "❌";
      default: return "⚪";
    }
  };

  const formatPrice = (quote) => {
    const price = quote.manualPrice || quote.totalPrice;
    if (!price) return "TBD";
    return `£${price.toLocaleString()}`;
  };

  const searchQuotes = (quotes, query) => {
    if (!query.trim()) return quotes;
    
    const q = query.trim().toLowerCase();

    // Price range: £10000-£15000 or 10000-15000
    const priceRange = q.match(/£?([\d,]+)\s*[-–]\s*£?([\d,]+)/);
    if (priceRange) {
      const min = parseFloat(priceRange[1].replace(/,/g, ''));
      const max = parseFloat(priceRange[2].replace(/,/g, ''));
      return quotes.filter(quote => {
        const price = quote.manualPrice || quote.totalPrice || 0;
        return price >= min && price <= max;
      });
    }

    // Date: "may 2026", "apr", "2026"
    const monthNames = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
    const isMonthSearch = monthNames.some(m => q.includes(m));
    const isYearSearch = /\b20\d{2}\b/.test(q);
    if (isMonthSearch || isYearSearch) {
      return quotes.filter(quote => {
        const dateStr = new Date(quote.createdAt)
          .toLocaleString('default', { month: 'short', year: 'numeric' })
          .toLowerCase();
        return dateStr.includes(q);
      });
    }

    // Status: fuzzy match e.g. "invoice" matches "INVOICED"
    const STATUS_LIST = [
      "PENDING","CONTACTED","LEFT_MESSAGE","SURVEY_BOOKED","SURVEY_COMPLETED",
      "REVISED_ESTIMATE_SENT","SALE_AGREED","INVOICED","PAYMENT_RECEIVED",
      "ORDERED","INSTALLED","COMPLETE","APPROVED","REJECTED"
    ];
    const matchedStatus = STATUS_LIST.find(s => s.toLowerCase().includes(q) || q.includes(s.toLowerCase().replace(/_/g, ' ')));
    if (matchedStatus) {
      return quotes.filter(quote => (quote.status || '').toUpperCase() === matchedStatus);
    }

    // Default: customer name
    return quotes.filter(quote =>
      (quote.customer?.name || '').toLowerCase().includes(q)
    );
  };

  const baseQuotes = sortCriteria === "timeframe" ? filteredQuotes : quotes;
  const displayQuotes = searchQuotes(baseQuotes, searchQuery);

  const getPositionDisplay = (pos) => {
    if (pos === null || pos === undefined) return 'N/A';
    if (typeof pos === 'string') {
      return pos.charAt(0).toUpperCase() + pos.slice(1).toLowerCase();
    }
    const positionMap = { 0: 'Front', 1: 'Back', 2: 'Left', 3: 'Right' };
    return positionMap[pos] || `Position ${pos}`;
  };

  const handleManualDataChange = (field) => (e) => {
    setManualData(prev => ({...prev, [field]: e.target.value}));
  };

  const handleManualSubmit = async () => {
    setIsSubmitting(true); 
    try {
      await createManualQuote({
        ...manualData,
        estimatedValue: parseFloat(manualData.estimatedValue) || 0
      });
      await fetchQuotes(); 
      setIsManualOpen(false);
      setManualData({ 
        customerName: '', email: '', phone: '', address: '',
        productType: 'Canopy - Wall Mounted', estimatedValue: '', 
        notes: '', width: '', length: '', color: '' 
      });
      showSuccessSnackbar(t('quotes.success.leadAdded'));
      clearQuotesCache();
    } catch (error) {
      console.error("Failed to add lead:", error);
      setSuccessMessage("Error: " + error.message);
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false); 
    }
  };

  // Reusable Component to render Site Survey answers inside collapsed panel
  const renderSiteSurveySection = (quote) => {
    if (!quote.siteSurvey) {
      // Safe fallback check to ensure no raw bracket text ever displays
      const translation = t('quotes.survey.noSurvey');
      const noSurveyText = (!translation || translation === 'quotes.survey.noSurvey') 
        ? "No Site Survey Submitted" 
        : translation;

      return (
        <Box sx={{ 
          mt: 3, 
          p: 4, 
          bgcolor: '#fbfbfb', 
          borderRadius: 2, 
          border: '1px dashed #ccc',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1
        }}>
          <AssignmentIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {noSurveyText}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ maxWidth: '320px' }}>
            The customer has not completed their site survey questionnaire for this configuration yet.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 3, p: 2.5, bgcolor: '#ffffff', border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#a8851d', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon fontSize="small" />
          {t('quotes.survey.sectionTitle') || "Completed Site Survey Details"}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {t('quotes.survey.submittedAt') || "Submitted on"}: {new Date(quote.siteSurvey.submittedAt).toLocaleString()}
        </Typography>
        
        <Grid container spacing={2}>
          {quote.siteSurvey.answers.map((ans, idx) => {
            const isImage = ans.questionType === 'ImageUpload' || (ans.answerValue && ans.answerValue.startsWith('http'));
            return (
              <Grid item xs={12} key={idx}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Q: {ans.questionText}
                </Typography>
                {isImage ? (
                  <Box sx={{ mt: 1, display: 'inline-block', border: '1px solid #eaeaea', borderRadius: 1, overflow: 'hidden' }}>
                    <a href={ans.answerValue} target="_blank" rel="noopener noreferrer">
                      <img 
                        src={ans.answerValue} 
                        alt="Survey upload" 
                        style={{ maxHeight: 150, maxWidth: '100%', objectFit: 'contain', display: 'block' }}
                      />
                    </a>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    A: {ans.answerValue || 'N/A'}
                  </Typography>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <NotesModal 
        open={notesModalOpen} 
        onClose={handleCloseNotesModal} 
        quote={selectedQuote}
        onSave={handleSaveNotes}
        onShowSuccess={showSuccessSnackbar}
        getAuthHeaders={getAuthHeaders}
      />

      <Dialog open={isManualOpen} onClose={() => setIsManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{t('quotes.manualDialog.title')}</DialogTitle>
        <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField 
                    label={t('quotes.manualDialog.customerName')} fullWidth 
                    value={manualData.customerName}
                    onChange={handleManualDataChange('customerName')}
                />
                <TextField 
                    label={t('quotes.manualDialog.email')} fullWidth 
                    value={manualData.email}
                    onChange={handleManualDataChange('email')}
                />
                <TextField 
                    label={t('quotes.manualDialog.phone')} fullWidth 
                    value={manualData.phone}
                    onChange={handleManualDataChange('phone')}
                />
                <TextField 
                    label={t('quotes.manualDialog.address')} fullWidth 
                    placeholder={t('quotes.manualDialog.addressPlaceholder')}
                    value={manualData.address} 
                    onChange={handleManualDataChange('address')}
                />
                <FormControl fullWidth>
                    <InputLabel>{t('quotes.manualDialog.productType')}</InputLabel>
                    <Select
                        value={manualData.productType}
                        label={t('quotes.manualDialog.productType')}
                        onChange={handleManualDataChange('productType')}
                    >
                        <MenuItem value="Canopy - Wall Mounted">{t('quotes.manualDialog.products.wallMounted')}</MenuItem>
                        <MenuItem value="Canopy - Free Standing">{t('quotes.manualDialog.products.freeStanding')}</MenuItem>
                        <MenuItem value="Other / Bespoke">{t('quotes.manualDialog.products.bespoke')}</MenuItem>
                    </Select>
                </FormControl>
                <TextField 
                    label={t('quotes.manualDialog.estimatedValue')} fullWidth type="number"
                    InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                    value={manualData.estimatedValue}
                    onChange={handleManualDataChange('estimatedValue')}
                />
                <TextField 
                    label={t('quotes.manualDialog.notes')} fullWidth multiline rows={4}
                    placeholder={t('quotes.manualDialog.notesPlaceholder')}
                    helperText={t('quotes.manualDialog.notesHelper')}
                    value={manualData.notes}
                    onChange={handleManualDataChange('notes')}
                    sx={{ 
                        '& .MuiOutlinedInput-root': { backgroundColor: '#f9fafb' }
                    }}
                />
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setIsManualOpen(false)} sx={{ color: 'gray' }}>{t('quotes.manualDialog.cancel')}</Button>
            <Button 
                variant="contained" 
                onClick={handleManualSubmit} 
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ 
                    bgcolor: '#a8851d', 
                    '&:hover': { bgcolor: '#8c6e18' },
                    px: 4
                }}
            >
              {isSubmitting ? t('quotes.manualDialog.saving') : t('quotes.manualDialog.save')}
            </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{t('quotes.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>{t('quotes.deleteDialog.body')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="secondary">
            {t('quotes.deleteDialog.cancel')}
          </Button>
          <Button onClick={handleDeleteQuote} color="error" autoFocus>
            {t('quotes.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={statusDialogOpen}
        onClose={handleCloseStatusDialog}
        aria-labelledby="status-dialog-title"
        aria-describedby="status-dialog-description"
      >
        <DialogTitle id="status-dialog-title">{t('quotes.statusDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('quotes.statusDialog.body')} "{newStatus}"? <br />
            {t('quotes.statusDialog.body2')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseStatusDialog} color="secondary">
            {t('quotes.statusDialog.cancel')}
          </Button>
          <Button onClick={handleStatusChange} color="primary" autoFocus>
            {t('quotes.statusDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          position: "fixed",
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1400,
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{
            width: "300px",
            fontSize: "0.9rem",
            textAlign: "center",
          }}
        >
          {successMessage || t('quotes.success.deleted')}
        </Alert>
      </Snackbar>

      <Paper elevation={0} sx={{ mb: 4, p: 3, bgcolor: 'grey.50' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              {t('quotes.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('quotes.subtitle')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControlLabel
                control={
                    <Checkbox
                        checked={selectedRows.length === quotes.length && quotes.length > 0}
                        indeterminate={selectedRows.length > 0 && selectedRows.length < quotes.length}
                        onChange={handleSelectAll}
                    />
                }
                label={`${selectedRows.length} ${t('quotes.selected')}`}
            />
            <Typography variant="body2" color="text.secondary">
              {totalCount} {t('quotes.total')} ({t('quotes.page')} {page} {t('quotes.of')} {totalPages})
            </Typography>

            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setIsManualOpen(true)}
              sx={{ bgcolor: '#a8851d', '&:hover': { bgcolor: '#8c6e18' } }}
            >
              {t('quotes.addManualLead')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <>
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Stack direction={isMobile ? "column" : "row"} spacing={3} alignItems="flex-start">
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(event, newView) => { if (newView !== null) setViewMode(newView); }}
                aria-label="view mode"
                >
                <ToggleButton value="card" aria-label="card view"><ViewModuleIcon /></ToggleButton>
                <ToggleButton value="list" aria-label="list view"><ViewListIcon /></ToggleButton>
              </ToggleButtonGroup>
              <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                  <InputLabel>{t('quotes.sortBy')}</InputLabel>
                  <Select 
                      value={sortCriteria} 
                      onChange={handleSortChange}
                      label={t('quotes.sortBy')}
                  >
                      <MenuItem value="name">{t('quotes.sort.nameAZ')}</MenuItem>
                      <MenuItem value="price">{t('quotes.sort.topPrices')}</MenuItem>
                      <MenuItem value="timeline">{t('quotes.sort.newest')}</MenuItem>
                      <MenuItem value="timeframe">{t('quotes.sort.byStatus')}</MenuItem>
                  </Select>
              </FormControl>

              {sortCriteria === "timeframe" && (
                <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                    <InputLabel>{t('quotes.filterStatus')}</InputLabel>
                    <Select 
                        value={selectedTimeframe}
                        onChange={handleTimeframeChange}
                        label={t('quotes.filterStatus')}
                    >
                        <MenuItem value="all">{t('quotes.status.all')}</MenuItem>
                        <MenuItem value="PENDING">{t('quotes.status.PENDING')}</MenuItem>
                        <MenuItem value="CONTACTED">{t('quotes.status.CONTACTED')}</MenuItem>
                        <MenuItem value="LEFT_MESSAGE">{t('quotes.status.LEFT_MESSAGE')}</MenuItem>
                        <MenuItem value="SURVEY_BOOKED">{t('quotes.status.SURVEY_BOOKED')}</MenuItem>
                        <MenuItem value="SURVEY_COMPLETED">{t('quotes.status.SURVEY_COMPLETED')}</MenuItem>
                        <MenuItem value="REVISED_ESTIMATE_SENT">{t('quotes.status.REVISED_ESTIMATE_SENT')}</MenuItem>
                        <MenuItem value="SALE_AGREED">{t('quotes.status.SALE_AGREED')}</MenuItem>
                        <MenuItem value="INVOICED">{t('quotes.status.INVOICED')}</MenuItem>
                        <MenuItem value="PAYMENT_RECEIVED">{t('quotes.status.PAYMENT_RECEIVED')}</MenuItem>
                        <MenuItem value="ORDERED">{t('quotes.status.ORDERED')}</MenuItem>
                        <MenuItem value="INSTALLED">{t('quotes.status.INSTALLED')}</MenuItem>
                        <MenuItem value="COMPLETE">{t('quotes.status.COMPLETE')}</MenuItem>
                        <MenuItem value="APPROVED">{t('quotes.status.APPROVED')}</MenuItem>
                        <MenuItem value="REJECTED">{t('quotes.status.REJECTED')}</MenuItem>
                    </Select>
                </FormControl>
            )}

            <Box sx={{ flexGrow: 1 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder='Search by name, status, date (e.g. "May 2026"), or price range (e.g. "£10000-£15000")'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      🔍
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        ✕
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&.Mui-focused fieldset': { borderColor: '#a8851d' }
                  }
                }}
              />
              {searchQuery && (
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                  {displayQuotes.length} result{displayQuotes.length !== 1 ? 's' : ''} for "{searchQuery}"
                </Typography>
              )}
            </Box>
            </Stack>
          </Paper>

          {viewMode === 'card' ? (
          <Grid container spacing={3}>
            {displayQuotes.map((quote, index) => ( 
              <Grid item xs={12} md={6} lg={4} key={quote.id}>
                <Fade in={true} timeout={300 + index * 100}>
                  <Card 
                    elevation={selectedRows.includes(quote.id) ? 8 : 2}
                    sx={{ 
                      height: '100%',
                      border: selectedRows.includes(quote.id) ? 2 : 0,
                      borderColor: 'primary.main',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        elevation: 6,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Checkbox
                            size="small"
                            checked={selectedRows.includes(quote.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleRowSelection(quote.id);
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            #{index + 1}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Chip
                            label={quote.status || "PENDING"}
                            color={getStatusColor(quote.status)}
                            size="small"
                            icon={<span>{getStatusIcon(quote.status)}</span>}
                          />
                          {quote.isManualEntry && (
                            <Chip
                              label="Manual"
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          )}
                          { (quote.internalNotes && quote.internalNotes.trim() !== '') && (
                          <Tooltip title="This quote has internal notes">
                            <IconButton size="small" onClick={() => handleOpenNotesModal(quote)}>
                              <NoteAddIcon  color="action"/>
                            </IconButton>
                          </Tooltip>
                        )}
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, quote.id)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Stack spacing={2} mb={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                            {quote.customer?.name || 'N/A'}
                          </Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PaymentIcon fontSize="small" color="action" />
                          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {formatPrice(quote)}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Stack spacing={1} mb={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {new Date(quote.createdAt).toLocaleDateString()}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <StraightenIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            {quote.width}m × {quote.length}m
                          </Typography>
                        </Stack>
                      </Stack>

                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <Select
                          value={(quote.status || "PENDING").toUpperCase()}
                          onChange={(e) => handleOpenStatusDialog(quote.id, e.target.value)}
                          sx={{
                            // DYNAMIC THEME COLORS APPLIED HERE
                            backgroundColor: getStatusThemeColor(quote.status).bg,
                            '& .MuiSelect-select': {
                              color: getStatusThemeColor(quote.status).text,
                              fontWeight: 700
                            }
                          }}
                        >
                          {[
                          "PENDING", "CONTACTED", "LEFT_MESSAGE", "SURVEY_BOOKED",
                          "SURVEY_COMPLETED", "REVISED_ESTIMATE_SENT", "SALE_AGREED",
                          "INVOICED", "PAYMENT_RECEIVED", "ORDERED", "INSTALLED",
                          "COMPLETE", "APPROVED", "REJECTED"
                        ].map((status) => (
                          <MenuItem key={status} value={status}>
                            {getStatusIcon(status)} {t(`quotes.status.${status}`).replace(/^[^\s]+\s/, '')}
                          </MenuItem>
                        ))}
                        </Select>
                      </FormControl>

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setOpenRow(openRow === index ? null : index)}
                        endIcon={openRow === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ mb: 1 }}
                      >
                        {openRow === index ? t('quotes.hideDetails') : t('quotes.viewDetails')}
                      </Button>

                      {openRow === index && (
                        <Fade in={true}>
                          <Box sx={{ mt: 2 }}>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                              {t('quotes.details.customerDetails')}
                            </Typography>
                            <Stack spacing={1} sx={{ mb: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <HomeIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {quote.customer?.address || 'N/A'}
                                </Typography>
                              </Stack>

                              <Stack direction="row" alignItems="center" spacing={1}>
                                <HomeIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  <strong>{t('quotes.details.postcode')}:</strong> {quote.customer?.postalCode || 'N/A'}
                                </Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <PhoneIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {quote.customer?.phone || 'N/A'}
                                </Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <EmailIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {quote.customer?.email || 'N/A'}
                                </Typography>
                              </Stack>
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                              {t('quotes.details.configDetails')}
                            </Typography>
                            <Stack spacing={1} sx={{ mb: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <BuildIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  <strong>{t('quotes.details.canopy')}:</strong> {quote.canopy?.name || 'N/A'}
                                </Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <BuildIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  <strong>{t('quotes.details.roof')}:</strong> {quote.roof?.name || 'N/A'}
                                </Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <PaletteIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  <strong>{t('quotes.details.color')}:</strong> {quote.color || 'N/A'}
                                </Typography>
                              </Stack>
                              
                              {quote.configurationWalls && quote.configurationWalls.length > 0 ? (
                                quote.configurationWalls.map((configWall, idx) => {
                                  const position = getPositionDisplay(configWall.position);
                                  const wallName = configWall.wall.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                  return (
                                    <Stack key={idx} direction="row" alignItems="center" spacing={1}>
                                      <BuildIcon fontSize="small" color="action" />
                                      <Typography variant="body2">
                                        <strong>Wall ({position}):</strong> {wallName}
                                      </Typography>
                                    </Stack>
                                  );
                                })
                              ) : (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <BuildIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.walls')}:</strong> N/A
                                  </Typography>
                                </Stack>
                              )}

                              {quote.accessoryConfigurations && quote.accessoryConfigurations.length > 0 ? (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <AssignmentIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.accessories')}:</strong> {quote.accessoryConfigurations.map(config => config.accessory.name).join(', ')}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  <AssignmentIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.accessories')}:</strong> N/A
                                  </Typography>
                                </Stack>
                              )}
                              
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2">
                                  <strong>{t('quotes.details.budget')}:</strong> {quote.budget || 'None'}
                                </Typography>
                              </Stack>

                              <Stack direction="row" alignItems="center" spacing={1}>
                                <BuildIcon fontSize="small" color="action" />
                                <Typography variant="body2">
                                  <strong>{t('quotes.details.groundCondition')}:</strong> {quote.groundCondition || 'N/A'}
                                </Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2">
                                  <strong>{t('quotes.details.customerNotes')}:</strong> {quote.notes || quote.customer?.notes || 'None'}
                                </Typography>
                              </Stack>
                            </Stack>

                            {/* INJECTED SITE SURVEY IN CARD VIEW */}
                            {renderSiteSurveySection(quote)}

                          </Box>
                        </Fade>
                      )}
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
          ) : (

          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayQuotes.map((quote, index) => (
                <React.Fragment key={quote.id}>
                  <TableRow 
                    hover 
                    onClick={() => setOpenRow(openRow === index ? null : index)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                          #{index + 1}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {quote.customer?.name || 'N/A'}
                        </Typography>
                        {quote.isManualEntry && (
                          <Chip
                            label="Manual"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ height: '20px', fontSize: '0.7rem' }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{new Date(quote.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>
                        {formatPrice(quote)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                          value={(quote.status || "PENDING").toUpperCase()}
                          onChange={(e) => handleOpenStatusDialog(quote.id, e.target.value)}
                          sx={{
                            // DYNAMIC THEME COLORS APPLIED HERE
                            backgroundColor: getStatusThemeColor(quote.status).bg,
                            '& .MuiSelect-select': {
                              color: getStatusThemeColor(quote.status).text,
                              fontWeight: 700,
                              py: 0.5
                            }
                          }}
                        >
                          {[
                            "PENDING", "CONTACTED", "LEFT_MESSAGE", "SURVEY_BOOKED",
                            "SURVEY_COMPLETED", "REVISED_ESTIMATE_SENT", "SALE_AGREED",
                            "INVOICED", "PAYMENT_RECEIVED", "ORDERED", "INSTALLED",
                            "COMPLETE", "APPROVED", "REJECTED"
                          ].map((status) => (
                            <MenuItem key={status} value={status}>
                              {t(`quotes.status.${status}`).replace(/^[^\s]+\s/, '')}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(e) => { e.stopPropagation(); handleMenuClick(e, quote.id); }}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                      <Collapse in={openRow === index} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
                          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {t('quotes.details.quoteDetails')}
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                                {t('quotes.details.customerInfo')}
                              </Typography>
                              <Stack spacing={1.5}>
                                <Stack direction="row" spacing={1}>
                                  <HomeIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {quote.customer?.address || 'N/A'}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <HomeIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.postcode')}</strong> {quote.customer?.postalCode || 'N/A'}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <PhoneIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {quote.customer?.phone || 'N/A'}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <EmailIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    {quote.customer?.email || 'N/A'}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
                                {t('quotes.details.configDetails')}
                              </Typography>
                              <Stack spacing={1.5}>
                                <Stack direction="row" spacing={1}>
                                  <StraightenIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.size')}:</strong> {quote.width}m × {quote.length}m
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <BuildIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.canopy')}</strong> {quote.canopy?.name || 'N/A'}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <BuildIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.roof')}</strong> {quote.roof?.name || 'N/A'}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <PaletteIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.color')}</strong> {quote.color || 'N/A'}
                                  </Typography>
                                </Stack>

                                {quote.configurationWalls && quote.configurationWalls.length > 0 ? (
                                  quote.configurationWalls.map((configWall, idx) => {
                                    const position = getPositionDisplay(configWall.position);
                                    const wallName = configWall.wall.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                    return (
                                      <Stack key={idx} direction="row" spacing={1}>
                                        <BuildIcon fontSize="small" color="action" />
                                        <Typography variant="body2">
                                          <strong>Wall ({position}):</strong> {wallName}
                                        </Typography>
                                      </Stack>
                                    );
                                  })
                                ) : (
                                  <Stack direction="row" spacing={1}>
                                    <BuildIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      <strong>{t('quotes.details.walls')}</strong> N/A
                                    </Typography>
                                  </Stack>
                                )}

                                {quote.accessoryConfigurations && quote.accessoryConfigurations.length > 0 ? (
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <AssignmentIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      <strong>{t('quotes.details.accessories')}</strong> {quote.accessoryConfigurations.map(config => config.accessory.name).join(', ')}
                                    </Typography>
                                  </Stack>
                                ) : (
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <AssignmentIcon fontSize="small" color="action" />
                                    <Typography variant="body2">
                                      <strong>{t('quotes.details.accessories')}:</strong> N/A
                                    </Typography>
                                  </Stack>
                                )}

                                <Stack direction="row" spacing={1}>
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.budget')}:</strong> {quote.budget || 'None'}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <AssignmentIcon fontSize="small" color="action" />
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.groundCondition')}:</strong> {quote.groundCondition || 'N/A'}
                                  </Typography>
                                </Stack>
                                <Stack direction="row" spacing={1}>
                                  <Typography variant="body2">
                                    <strong>{t('quotes.details.customerNotes')}:</strong> {quote.notes || quote.customer?.notes || 'None'}
                                  </Typography>
                                </Stack>
                              </Stack>
                            </Grid>
                          </Grid>

                          <Divider sx={{ my: 3 }} />

                          {/* INJECTED SITE SURVEY IN TABLE LIST VIEW */}
                          {renderSiteSurveySection(quote)}

                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
          >
            <MenuItem onClick={() => {
              const quote = quotes.find(q => q.id === menuQuoteId);
              handleOpenNotesModal(quote);
            }}>
              <NoteAddIcon sx={{ mr: 1 }} color="primary" />
                {t('quotes.addNotes')}
            </MenuItem>
            <MenuItem onClick={() => handleOpenDeleteDialog(menuQuoteId)}>
              <DeleteIcon sx={{ mr: 1 }} color="error" />
              {t('quotes.deleteQuote')}
            </MenuItem>
          </Menu>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination 
                count={totalPages} 
                page={page} 
                onChange={(event, value) => setPage(value)} 
                color="primary" 
                size={isMobile ? "small" : "large"}
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default QuoteManagement;